import { Search, X } from "lucide-react";
import { ReactNode, useState } from "react";

import { IgBadge } from "@intelligems/ig-theme/IgBadge";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgCheckbox } from "@intelligems/ig-theme/IgCheckbox";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgLoading } from "@intelligems/ig-theme/IgLoading";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, FormControlLabel, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";

export interface ShopifyItemPickerItem {
  /** Persisted value (e.g. a collection GID, or the tag/vendor string itself). */
  id: string;
  /** Merchant-facing label shown in the lists. */
  label: string;
}

interface ShopifyItemPickerProps {
  title: string;
  itemNoun: { singular: string; plural: string };
  searchPlaceholder: string;
  saveLabel: string;
  items: ShopifyItemPickerItem[];
  loading?: boolean;
  saveDisabled?: boolean;
  /**
   * Most items the caller will accept. The list still toggles freely — the
   * footer names the limit and Save waits until the selection is within it,
   * so a merchant can swap one item for another without deselecting first.
   */
  maxSelected?: number;
  /**
   * Selection to start from. Entries whose id is missing from `items` (a
   * deleted collection, a stale saved value) stay selected under their given
   * label until removed.
   */
  initialSelected?: ShopifyItemPickerItem[];
  onClose: () => void;
  onSave: (selected: ShopifyItemPickerItem[]) => void;
}

// Search results are capped so a store with thousands of tags never renders
// thousands of rows; search narrows the rest.
const MAX_RENDERED_ITEMS = 250;

/**
 * Generic multi-select picker for flat Shopify items (tags, collections,
 * vendors, …), visually modeled on the shared ProductPicker: item list with
 * search on the left, running selection on the right, actions in the footer.
 * Callers own the Dialog wrapper and the data (see useShopifyPickerItems).
 */
export const ShopifyItemPicker = ({
  title,
  itemNoun,
  searchPlaceholder,
  saveLabel,
  items,
  loading,
  saveDisabled,
  maxSelected,
  initialSelected,
  onClose,
  onSave,
}: ShopifyItemPickerProps) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ShopifyItemPickerItem[]>(
    () => initialSelected ?? []
  );

  const selectedIds = new Set(selected.map((item) => item.id));
  const overLimit = maxSelected !== undefined && selected.length > maxSelected;
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = normalizedSearch
    ? items.filter((item) =>
        item.label.toLowerCase().includes(normalizedSearch)
      )
    : items;
  const rendered = filtered.slice(0, MAX_RENDERED_ITEMS);
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((item) => selectedIds.has(item.id));
  const someFilteredSelected = filtered.some((item) =>
    selectedIds.has(item.id)
  );

  const toggleItem = (item: ShopifyItemPickerItem) => {
    setSelected((current) =>
      current.some((entry) => entry.id === item.id)
        ? current.filter((entry) => entry.id !== item.id)
        : [...current, item]
    );
  };

  const toggleAllFiltered = () => {
    setSelected((current) => {
      if (allFilteredSelected) {
        const filteredIds = new Set(filtered.map((item) => item.id));
        return current.filter((entry) => !filteredIds.has(entry.id));
      }
      const currentIds = new Set(current.map((entry) => entry.id));
      return [
        ...current,
        ...filtered.filter((item) => !currentIds.has(item.id)),
      ];
    });
  };

  const removeItem = (id: string) => {
    setSelected((current) => current.filter((entry) => entry.id !== id));
  };

  let listContent: ReactNode;
  if (loading && items.length === 0) {
    listContent = (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <IgLoading />
      </Stack>
    );
  } else if (filtered.length === 0) {
    listContent = (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <IgTypography color="text.secondary">
          No {itemNoun.plural} found
        </IgTypography>
      </Stack>
    );
  } else {
    listContent = (
      <Box sx={{ flex: 1, overflowY: "auto", px: 5, pb: 3 }}>
        {rendered.map((item) => (
          <FormControlLabel
            key={item.id}
            sx={{ display: "flex", mx: 0, minWidth: 0 }}
            control={
              <IgCheckbox
                size="small"
                checked={selectedIds.has(item.id)}
                onChange={() => toggleItem(item)}
              />
            }
            label={
              <IgTypography noWrap sx={{ minWidth: 0 }}>
                {item.label}
              </IgTypography>
            }
          />
        ))}
        {filtered.length > rendered.length && (
          <IgTypography color="text.secondary" sx={{ py: 2 }}>
            Showing the first {MAX_RENDERED_ITEMS} of {filtered.length} matches
            — refine your search to see the rest.
          </IgTypography>
        )}
      </Box>
    );
  }

  return (
    <Grid
      container
      direction="column"
      sx={{
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        minWidth: "fit-content",
      }}
    >
      <Grid
        container
        direction="row"
        padding={6}
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: `1px solid ${theme.igBorders.core.default}` }}
      >
        <Grid>
          <IgTypography variant="h3" fontWeight={600}>
            {title}
          </IgTypography>
        </Grid>
        <Grid sx={{ height: "24px", width: "24px" }}>
          <IgIconButton aria-label="Close" onClick={onClose}>
            <X size={18} />
          </IgIconButton>
        </Grid>
      </Grid>
      <Grid container direction="row" wrap="nowrap" sx={{ height: "460px" }}>
        <Grid sx={{ flex: 1, minWidth: 0 }}>
          <Stack sx={{ height: "100%" }}>
            <Box sx={{ px: 5, pt: 5 }}>
              <IgTextField
                fullWidth
                placeholder={searchPlaceholder}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                slotProps={{
                  input: { endAdornment: <Search size={16} /> },
                }}
              />
            </Box>
            <Box sx={{ px: 5, pt: 2 }}>
              <FormControlLabel
                sx={{ mx: 0 }}
                control={
                  <IgCheckbox
                    size="small"
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected && !allFilteredSelected}
                    disabled={filtered.length === 0}
                    onChange={toggleAllFiltered}
                  />
                }
                label={<IgTypography>Select all</IgTypography>}
              />
            </Box>
            {listContent}
          </Stack>
        </Grid>
        <Grid
          sx={{
            width: "300px",
            borderLeft: `1px solid ${theme.igBorders.core.default}`,
          }}
        >
          <Stack sx={{ height: "100%", pt: "14px" }}>
            <Grid px={7} container direction="row" columnSpacing={2}>
              <Grid mr={3}>
                <IgTypography color={theme.palette.text.secondary}>
                  Selected
                </IgTypography>
              </Grid>
              <Grid>
                <IgBadge badgeContent={selected.length} color="accent" />
              </Grid>
            </Grid>
            <Grid px={3}>
              <IgDivider spacing="s" />
            </Grid>
            <Box sx={{ flex: 1, overflowY: "auto", pl: 5, pr: 3 }}>
              {selected.map((item) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ minWidth: 0, py: 1 }}
                >
                  <IgTypography noWrap sx={{ minWidth: 0 }}>
                    {item.label}
                  </IgTypography>
                  <IgIconButton
                    aria-label={`Remove ${item.label}`}
                    onClick={() => removeItem(item.id)}
                  >
                    <X size={14} />
                  </IgIconButton>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        sx={{
          borderTop: `1px solid ${theme.igBorders.core.default}`,
          height: "66px",
          paddingX: 6,
          paddingY: 4,
        }}
      >
        <Grid>
          <IgTypography color={overLimit ? "error" : undefined}>
            {selected.length}{" "}
            {selected.length === 1 ? itemNoun.singular : itemNoun.plural}{" "}
            selected
            {overLimit
              ? ` — pick up to ${maxSelected} ${
                  maxSelected === 1 ? itemNoun.singular : itemNoun.plural
                }`
              : ""}
          </IgTypography>
        </Grid>
        <Grid columnSpacing={3} container direction="row">
          <Grid>
            {/* The picker footer's pair sits on 12px sides, with Cancel on the
                neutral fill rather than bare text. */}
            <IgButton
              variant="contained"
              color="secondary"
              onClick={onClose}
              sx={{ px: 3 }}
            >
              Cancel
            </IgButton>
          </Grid>
          <Grid>
            <IgButton
              variant="contained"
              disabled={saveDisabled || overLimit}
              onClick={() => onSave(selected)}
              sx={{ px: 3 }}
            >
              {saveLabel}
            </IgButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
